import {
    Box,
    Container,
    Grid,
    Typography,
    useMediaQuery,
    useTheme,
    alpha
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion, Variants, useScroll, useTransform } from 'framer-motion';
import React, { useRef } from 'react';
import { getOrDefault } from '../../../../Helpers/Utils';
import { AboutCompanyProps, CompanyValue, AboutCompanySpec } from './types';
import { useMicrospecThemeLanguageContext } from '../../../../Themes';

// Glassmorphic value card
const ValueCard = styled(motion.div)(({ theme }) => ({
    position: 'relative',
    height: '100%',
    borderRadius: 24,
    padding: theme.spacing(4),
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',

    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(255,255,255,0.5), transparent)',
        opacity: 0,
        transition: 'opacity 0.5s ease',
        zIndex: 0
    },

    '&:hover': {
        transform: 'translateY(-16px) scale(1.03)',
        boxShadow: '0 24px 64px 0 rgba(0, 0, 0, 0.15)',
        background: 'rgba(255, 255, 255, 0.85)',
        '&::before': {
            opacity: 1
        }
    }
}));

// Animated icon wrapper
const IconWrapper = styled(Box)(({ theme }) => ({
    width: 80,
    height: 80,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '2.5rem',
    marginBottom: theme.spacing(3),
    position: 'relative',
    background: 'rgba(255, 255, 255, 0.5)',
    backdropFilter: 'blur(10px)',
    border: '2px solid rgba(255, 255, 255, 0.5)',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',

    '&::after': {
        content: '""',
        position: 'absolute',
        inset: -8,
        borderRadius: '50%',
        background: 'conic-gradient(from 0deg, transparent, currentColor, transparent)',
        opacity: 0,
        transition: 'opacity 0.4s ease',
        animation: 'rotate 3s linear infinite',
        zIndex: -1
    },

    '@keyframes rotate': {
        '0%': { transform: 'rotate(0deg)' },
        '100%': { transform: 'rotate(360deg)' }
    }
}));

const AboutCompany: React.FC<AboutCompanyProps> = ({ spec, type, index }) => {
    const theme = useTheme();
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const imageRef = useRef<HTMLDivElement>(null);

    // Parallax scroll effect for image
    const { scrollYProgress } = useScroll({
        target: imageRef,
        offset: ["start end", "end start"]
    });
    const imageY = useTransform(scrollYProgress, [0, 1], [100, -100]);
    const imageScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.9, 1, 0.9]);

    // Component configuration
    const componentSpec: AboutCompanySpec = getOrDefault(spec, {});
    const backgroundColor = getOrDefault(componentSpec.backgroundColor, theme.palette.background.default);
    const sectionTitle = localizationProvider.translateByType(
        getOrDefault(componentSpec.sectionTitle?.localizationObject, 'About Our Company')
    );
    const titleColor = getOrDefault(componentSpec.titleColor, theme.palette.text.primary);
    const textColor = getOrDefault(componentSpec.textColor, theme.palette.text.secondary);
    const accentColor = getOrDefault(componentSpec.accentColor, theme.palette.primary.main);

    // Content
    const companyName = localizationProvider.translateByType(
        getOrDefault(componentSpec.companyName?.localizationObject, 'Our Company')
    );
    const description = localizationProvider.translateByType(
        getOrDefault(componentSpec.description?.localizationObject, 'We are a leading company dedicated to providing exceptional services and creating memorable experiences for our clients. Our commitment to excellence and innovation sets us apart in the industry.')
    );
    const companyImage = getOrDefault(componentSpec.companyImage, '/static/images/travel.png');

    const valuesCardsTitleColor = getOrDefault(componentSpec.valuesCardsTitleColor, theme.palette.text.primary);
    const valuesCardsTextColor = getOrDefault(componentSpec.valuesCardsTextColor, theme.palette.text.secondary);

    // Values/Features
    const values = getOrDefault(componentSpec.values, [
        { title: 'Innovation', description: 'Constantly evolving with latest technologies', icon: '💡' },
        { title: 'Quality', description: 'Delivering excellence in every project', icon: '⭐' },
        { title: 'Customer Focus', description: 'Putting our clients at the center', icon: '🎯' },
        { title: 'Integrity', description: 'Building trust through transparency', icon: '🤝' }
    ]);

    // Animation variants
    const containerVariants: Variants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.12,
                delayChildren: 0.15
            }
        }
    };

    const itemVariants: Variants = {
        hidden: {
            opacity: 0,
            y: 50,
            scale: 0.95
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                type: "spring" as const,
                stiffness: 100,
                damping: 15
            }
        }
    };

    const textRevealVariants: Variants = {
        hidden: {
            opacity: 0,
            x: -50
        },
        visible: {
            opacity: 1,
            x: 0,
            transition: {
                duration: 0.8,
                ease: "easeOut" as const
            }
        }
    };

    const imageRevealVariants: Variants = {
        hidden: {
            opacity: 0,
            scale: 0.8,
            rotateY: -15
        },
        visible: {
            opacity: 1,
            scale: 1,
            rotateY: 0,
            transition: {
                duration: 1,
                ease: "easeOut" as const
            }
        }
    };

    return (
        <Box
            id={spec.id}
            sx={{
                background: backgroundColor,
                minHeight: '100vh',
                py: { xs: 6, md: 12 },
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Animated background elements */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '20%',
                    left: '-10%',
                    width: 600,
                    height: 600,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(accentColor, 0.1)}, transparent)`,
                    filter: 'blur(80px)',
                    animation: 'float-slow 25s ease-in-out infinite',
                    '@keyframes float-slow': {
                        '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                        '50%': { transform: 'translate(100px, 100px) scale(1.1)' }
                    }
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '10%',
                    right: '-10%',
                    width: 500,
                    height: 500,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(accentColor, 0.08)}, transparent)`,
                    filter: 'blur(60px)',
                    animation: 'float-reverse-slow 20s ease-in-out infinite',
                    '@keyframes float-reverse-slow': {
                        '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                        '50%': { transform: 'translate(-80px, -80px) scale(1.15)' }
                    }
                }}
            />

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                {/* Section Title */}
                {sectionTitle && (
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 12 } }}>
                            <Typography
                                variant="h1"
                                sx={{
                                    color: titleColor,
                                    fontWeight: 900,
                                    fontSize: { xs: '2.5rem', md: '4rem', lg: '5rem' },
                                    lineHeight: 1.1,
                                    mb: 2,
                                    letterSpacing: '-0.02em',
                                    position: 'relative',
                                    display: 'inline-block',
                                    background: `linear-gradient(135deg, ${titleColor}, ${accentColor})`,
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: -16,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: 120,
                                        height: 6,
                                        borderRadius: 3,
                                        background: accentColor,
                                        boxShadow: `0 4px 12px ${alpha(accentColor, 0.3)}`
                                    }
                                }}
                            >
                                {sectionTitle}
                            </Typography>
                        </Box>
                    </motion.div>
                )}

                {/* Main Content */}
                <Grid container spacing={{ xs: 4, md: 8 }} sx={{ mb: { xs: 8, md: 12 } }}>
                    {/* Company Description */}
                    <Grid item xs={12} md={6}>
                        <motion.div
                            variants={textRevealVariants}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <Box
                                sx={{
                                    pr: { md: 4 },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    height: '100%'
                                }}
                            >
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.2, duration: 0.6 }}
                                >
                                    <Typography
                                        variant="h2"
                                        sx={{
                                            color: titleColor,
                                            fontWeight: 800,
                                            fontSize: { xs: '2rem', md: '3rem', lg: '3.5rem' },
                                            mb: 4,
                                            lineHeight: 1.2,
                                            letterSpacing: '-0.01em'
                                        }}
                                    >
                                        {companyName}
                                    </Typography>
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.4, duration: 0.6 }}
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: textColor,
                                            fontSize: { xs: '1.1rem', md: '1.3rem' },
                                            lineHeight: 1.9,
                                            mb: 4
                                        }}
                                    >
                                        {description}
                                    </Typography>
                                </motion.div>
                            </Box>
                        </motion.div>
                    </Grid>

                    {/* Company Image with Parallax */}
                    <Grid item xs={12} md={6}>
                        <motion.div
                            ref={imageRef}
                            variants={imageRevealVariants}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <motion.div
                                style={{
                                    y: isMobile ? 0 : imageY,
                                    scale: imageScale
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'relative',
                                        borderRadius: 4,
                                        overflow: 'hidden',
                                        height: { xs: 300, sm: 400, md: 500 },
                                        boxShadow: `0 24px 80px ${alpha(accentColor, 0.2)}`,
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background: `linear-gradient(135deg, ${alpha(accentColor, 0.1)} 0%, transparent 100%)`,
                                            zIndex: 2,
                                            pointerEvents: 'none'
                                        },
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            inset: 0,
                                            border: `2px solid ${alpha(accentColor, 0.2)}`,
                                            borderRadius: 4,
                                            zIndex: 3,
                                            pointerEvents: 'none'
                                        }
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={companyImage}
                                        alt={companyName}
                                        onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                                            e.currentTarget.style.display = 'none';
                                        }}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            position: 'relative',
                                            zIndex: 1,
                                            transition: 'transform 0.6s ease',
                                            '&:hover': {
                                                transform: 'scale(1.05)'
                                            }
                                        }}
                                    />
                                </Box>
                            </motion.div>
                        </motion.div>
                    </Grid>
                </Grid>

                {/* Company Values */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                >
                    <Grid container spacing={{ xs: 3, md: 4 }}>
                        {values.map((value: CompanyValue, idx: number) => (
                            <Grid item xs={12} sm={6} md={3} key={idx}>
                                <motion.div
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <ValueCard
                                        sx={{
                                            color: accentColor
                                        }}
                                        onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                                            const icon = e.currentTarget.querySelector('.icon-wrapper') as HTMLElement;
                                            if (icon) {
                                                icon.style.transform = 'scale(1.1) rotate(10deg)';
                                            }
                                        }}
                                        onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                                            const icon = e.currentTarget.querySelector('.icon-wrapper') as HTMLElement;
                                            if (icon) {
                                                icon.style.transform = 'scale(1) rotate(0deg)';
                                            }
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                zIndex: 2,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                textAlign: 'center',
                                                minHeight: 220
                                            }}
                                        >
                                            <IconWrapper className="icon-wrapper">
                                                {value.icon}
                                            </IconWrapper>

                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    color: valuesCardsTitleColor,
                                                    fontWeight: 700,
                                                    mb: 2,
                                                    fontSize: '1.3rem',
                                                    lineHeight: 1.3
                                                }}
                                            >
                                                {value.title}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: valuesCardsTextColor,
                                                    lineHeight: 1.7,
                                                    fontSize: '0.95rem'
                                                }}
                                            >
                                                {value.description}
                                            </Typography>
                                        </Box>

                                        {/* Decorative corner gradient */}
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                bottom: -15,
                                                right: -15,
                                                width: 150,
                                                height: 150,
                                                borderRadius: '40%',
                                                background: `radial-gradient(circle at bottom right, ${alpha(accentColor, 0.15)}, transparent)`,
                                                pointerEvents: 'none',
                                                zIndex: 1
                                            }}
                                        />
                                    </ValueCard>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>
            </Container>
        </Box>
    );
};

export default AboutCompany;
